import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from 'components/CncUI/Typography';

import useOktaWidget from '../hooks/useOktaWidget';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import cncLogo from 'images/CnCLogoRegister.svg';

import SupportFooter from 'components/SupportFooter';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    maxWidth: 1180,
    margin: 'auto',
    [breakpoints.up('md')]: {
      paddingTop: 94,
    },
  },
  leftPanel: {
    backgroundImage: 'linear-gradient(180deg, #3333FF 0%, #000080 100%)',
    boxShadow:
      '0px 1px 5px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.12)',
    [breakpoints.up('md')]: {
      height: 630,
    },
  },
  rightPanel: {
    padding: '6px 0',
    [breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  paper: {
    borderRadius: 0,
    padding: '16px 16px 24px',
    [breakpoints.up('md')]: {
      height: 578,
    },
  },
  innerLeft: {
    height: '100%',
    color: palette.cncUI.white,
    padding: '8px 16px',
    wordBreak: 'break-word',
    [breakpoints.up('md')]: {
      padding: 40,
    },
    [breakpoints.down('sm')]: {
      maxWidth: 502,
      alignItems: 'center',
      marginLeft: 'auto',
      marginRight: 'auto',
      height: 'auto',
    },
  },
  logo: {
    [breakpoints.down('sm')]: {
      textAlign: 'center',
    },
    '& img': {
      [breakpoints.up('sm')]: {
        marginLeft: 2,
        width: 340,
      },
      [breakpoints.down('sm')]: {
        width: 280,
        marginTop: 40,
      },
    },
  },
  footer: {
    margin: '32px auto',
    maxWidth: 560,
    textAlign: 'center',
    [breakpoints.down('sm')]: {
      padding: '0 8px',
      marginTop: 16,
    },
    '& .cncui-typography': {
      color: palette.cncUI.primary.gray['+3'],
    },
  },
  hidden: {
    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  widget: {
    placeSelf: 'center',
    animationName: 'loginOpacity',
    animationDuration: '0.3s',
    animationTimingFunction: 'ease-in',
    marginTop: 32,
    [breakpoints.up('md')]: {
      marginTop: 90,
    },
    '& .auth-container': {
      margin: '0 auto !important',
      border: 'none !important',
      boxShadow: 'none !important',
    },
    '& .auth-header': {
      display: 'none',
    },
    '& .okta-form-title': {
      fontSize: '24px !important',
      fontWeight: '400 !important',
    },
    '& .link': {
      fontSize: '14px !important',
      fontWeight: '400 !important',
      color: `${palette.cncUI.primary.gray.primary} !important`,
    },
  },
  noHeight: {
    height: 0,
  },
  mobileExpand: {
    position: 'relative',
    backgroundColor: palette.cncUI.default['+6'],
    color: palette.cncUI.white,
    minHeight: 24,
    width: 24,
    height: 24,
    alignContent: 'center',
    justifyContent: 'center',
    top: -12,
    borderRadius: '50%',
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  title: {
    marginTop: 64,
    [breakpoints.down('sm')]: {
      marginTop: 40,
      textAlign: 'center',
    },
  },
  description: {
    marginTop: 16,
    [breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  hint: {
    marginTop: 40,
    [breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  emailBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: palette.cncUI.default.table,
    padding: 16,
    borderRadius: 2,
    color: palette.cncUI.primary.gray['+0'],
    marginTop: 16,
    marginBottom: 40,
  },
}));

const InOurSystem = props => {
  useOktaWidget();
  const classes = useStyles();
  const { state, isMobile } = props;
  const { data, form } = state;

  const maskedEmail = () => {
    const parts =
      (data && data.email && data.email.split('@')) ||
      (form && form.email && form.email.split('@'));
    const first =
      parts && parts[0] && parts[0].length >= 5
        ? `${parts[0].substring(0, 3)}${'*'.repeat(
            parts[0].length - 3,
          )}${parts[0].substring(parts[0].length - 1)}`
        : `${parts[0][0]}${'*'.repeat(parts[0].length - 1)}`;
    const host = parts[1].split('.');
    host[host.length - 1] = '*'.repeat(host[host.length - 1].length);
    return `${first}@${host.join('.')}`;
  };

  return (
    <>
      <Grid container spacing={0} className={classes.root} id="InOurSystem">
        <Grid item xs={12} md={6} className={classes.leftPanel}>
          <Grid
            container
            spacing={0}
            justifyContent="space-between"
            direction="column"
            className={classes.innerLeft}
          >
            <Grid item>
              <Grid container spacing={0}>
                <Grid item xs={12} className={classes.logo}>
                  <img src={cncLogo} alt="CastandCrew Logo" />
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.title} noMargin>
                <Typography variant="h3" white>
                  You’re Already Registered
                </Typography>
              </Grid>
              <Grid
                item
                noMargin
                xs={12}
                className={classes.description}
                id="InOurSystemMessage"
              >
                <Typography variant="body2" size="short" white>
                  Good news! You’ve already registered with MyCast&Crew. Please
                  log in to view your pay stubs, tax documents, and other
                  supporting documents.
                </Typography>
              </Grid>
              <Grid item xs={12} className={classes.hint}>
                <Typography size="short" medium white>
                  Hint: This is the email address you previously registered
                  with.
                </Typography>
                <div className={classes.emailBox}>
                  <Typography variant="body2" size="short">
                    {maskedEmail()}
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {isMobile && (
          <Grid item xs={12} className={classes.noHeight}>
            <div className={classes.mobileExpand}>
              <ExpandMoreIcon />
            </div>
          </Grid>
        )}
        <Grid item xs={12} md={6} className={classes.rightPanel}>
          <Paper className={classes.paper}>
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <div className={classes.widget} id="sign-in-widget" />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {!isMobile && (
        <div className={classes.footer}>
          <Typography variant="caption" noMargin>
            The privacy and security of your data is important to us. We use
            128-bit encryption to keep your data safe and will never sell your
            data to any third-parties.
          </Typography>
        </div>
      )}
      <SupportFooter />
    </>
  );
};

export default InOurSystem;
